import React, { useState } from "react";
import { Clock, History } from "lucide-react";
import { toast } from "sonner";
import { api, fmtEUR, formatApiError } from "../../lib/api";
import { Input } from "../ui/input";
import { fmtDateTime, fmtDur, toLocalInput, romeInputToIso } from "./dispoUtils";

const Row = ({ label, value, testid, tone = "text-slate-100" }) => (
  <div className="flex items-center justify-between text-[13px] py-1">
    <span className="text-slate-400">{label}</span>
    <span className={`font-mono-num font-bold ${tone}`} data-testid={testid}>{value}</span>
  </div>
);
const ACT = { start: "Avvio", end: "Chiusura", correction: "Correzione" };

function Correction({ b, onSaved }) {
  const [s, setS] = useState(toLocalInput(b.dispo_started_at));
  const [e, setE] = useState(toLocalInput(b.dispo_ended_at));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/dispo/${b.id}/correct`, { started_at: romeInputToIso(s), ended_at: romeInputToIso(e), note });
      onSaved(r.data.booking);
      setNote("");
      toast.success("Correzione salvata e importi ricalcolati");
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const cls = "bg-[#0C121E] border-slate-800/80 h-9 text-xs";
  return (
    <div className="space-y-2 pt-2 border-t border-slate-800" data-testid="dispo-correction">
      <div className="text-[10px] uppercase tracking-widest text-slate-500">Correzione orari (ora di Roma, tracciata)</div>
      <div className="grid grid-cols-1 gap-2">
        <Input type="datetime-local" value={s} onChange={(x) => setS(x.target.value)} className={cls} data-testid="dispo-correct-start" />
        <Input type="datetime-local" value={e} onChange={(x) => setE(x.target.value)} className={cls} data-testid="dispo-correct-end" />
      </div>
      <Input placeholder="Motivo della correzione" value={note} onChange={(x) => setNote(x.target.value)} className={cls} data-testid="dispo-correct-note" />
      <button type="button" disabled={busy || !note.trim() || !s} onClick={save} data-testid="dispo-correct-save"
        className="w-full rounded-lg py-2 text-xs font-semibold border border-amber-500/50 text-amber-200 hover:bg-amber-500/10 disabled:opacity-40">Salva correzione e ricalcola</button>
    </div>
  );
}

export function DispoAdminPanel({ booking: b, shareRate = 50, onSaved, external = false }) {
  const log = b.dispo_log || [];
  return (
    <div className="rounded-2xl border border-amber-500/30 bg-[#0A0907] p-4 space-y-2" data-testid="dispo-admin-panel">
      <div className="text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold inline-flex items-center gap-1.5"><Clock size={12} /> DISPO · Orari e calcoli</div>
      {(b.dispo_client_rate == null || (!external && b.dispo_driver_rate == null)) && (
        <div className="text-[11px] text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-lg p-2" data-testid="dispo-admin-missing-rates">
          Tariffe orarie mancanti: alla chiusura verranno registrati orari e durata, mentre gli importi mancanti restano quelli inseriti in prenotazione.
        </div>
      )}
      <Row label="Inizio previsto" value={b.time || "—"} />
      <Row label="Inizio effettivo" value={fmtDateTime(b.dispo_started_at)} testid="dispo-admin-start" />
      <Row label="Fine effettiva" value={b.dispo_ended_at ? fmtDateTime(b.dispo_ended_at) : "Non definita"} testid="dispo-admin-end" />
      <Row label="Durata effettiva" value={fmtDur(b.dispo_duration_min)} testid="dispo-admin-duration" />
      <Row label="Ore fatturabili" value={b.dispo_billable_hours ?? "—"} testid="dispo-admin-billable" tone="text-amber-300" />
      <div className="border-t border-slate-800 my-1" />
      <Row label="Tariffa oraria cliente" value={`${fmtEUR(b.dispo_client_rate || 0)} / ora`} />
      {!external && <Row label="Tariffa oraria driver" value={`${fmtEUR(b.dispo_driver_rate || 0)} / ora`} />}
      {external && <Row label="Prezzo per fornitore" value={fmtEUR(b.supplier_price || 0)} testid="dispo-admin-supplier-price" />}
      {b.dispo_ended_at && <>
        <Row label={b.dispo_client_rate != null ? `Totale cliente (${b.dispo_billable_hours} × ${fmtEUR(b.dispo_client_rate)})` : "Totale cliente (importo fisso)"} value={fmtEUR(b.price)} testid="dispo-admin-client-total" tone="text-emerald-300" />
        {!external && <>
          <Row label={b.dispo_driver_rate != null ? `Prezzo driver (${b.dispo_billable_hours} × ${fmtEUR(b.dispo_driver_rate)})` : "Prezzo driver (importo fisso)"} value={fmtEUR(b.driver_price)} testid="dispo-admin-driver-total" />
          <Row label={`Compenso autista ${shareRate}%`} value={fmtEUR(b.driver_cost)} testid="dispo-admin-driver-cost" tone="text-sky-300" />
        </>}
        <Row label="Margine lordo (prima di IVA e altri costi)" value={fmtEUR(b.margin)} testid="dispo-admin-margin" tone="text-amber-300" />
      </>}
      {log.length > 0 && (
        <div className="pt-2 border-t border-slate-800" data-testid="dispo-admin-log">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 inline-flex items-center gap-1 mb-1"><History size={11} /> Storico</div>
          {log.map((l, i) => (
            <div key={i} className="text-[11px] text-slate-400">{fmtDateTime(l.at)} · {ACT[l.action] || l.action} · {l.by}{l.note ? ` — ${l.note}` : ""}{l.action === "end" ? ` (${l.hours} h fatt.)` : ""}</div>
          ))}
        </div>
      )}
      {b.dispo_started_at && <Correction b={b} onSaved={onSaved} />}
      {!b.dispo_started_at && <div className="text-[11px] text-slate-500">L'ora di fine non è obbligatoria: durata e importi vengono calcolati quando il driver termina la disposizione.</div>}
    </div>
  );
}
